// The fixed, full-window card the chart pages show in full screen. Its padding
// is 16 px (the old p: 2) or, on a phone, the notch / home-indicator inset if
// that is larger (env() is 0 everywhere else, so desktops render as before).
// With `fill` the card is a column, and the chart (ChartStack `fill`) takes
// whatever is left under the toolbar and the values strip.
export const fullscreenCardSx = (zIndex: number, fill = false) =>
    ({
        position: 'fixed',
        inset: 0,
        zIndex,
        pt: 'max(16px, env(safe-area-inset-top, 0px))',
        pb: 'max(16px, env(safe-area-inset-bottom, 0px))',
        pl: 'max(16px, env(safe-area-inset-left, 0px))',
        pr: 'max(16px, env(safe-area-inset-right, 0px))',
        borderRadius: 0,
        overflowY: 'auto',
        bgcolor: 'background.default',
        ...(fill && { display: 'flex', flexDirection: 'column' }),
    }) as const;

export const MIN_FULLSCREEN_CHART_HEIGHT = 320;
// What the full-screen card holds besides the chart: its padding, the
// toolbar, the panel header, the time axis ...
export const FULLSCREEN_CHROME = 96;
// ... and, where the chart fills what is left (below), a first guess for the
// toolbar row and the values strip, used until the browser has laid them out.
export const FULLSCREEN_CHROME_COMPACT = 260;
// A phone on its side is 360 to 430 px high: the controls take most of it.
// Where the chart fills what is left it can go down to this floor instead of
// the 320 px minimum that makes the card scroll.
export const FULLSCREEN_MIN_COMPACT = 96;

/**
 * The height of the chart in full screen, and the floor to give the stack
 * (undefined: its usual one).
 *
 * `fill` is for a chart that takes what is left under the controls: below
 * 900 px, and on any touch screen, whose 44 px toolbar buttons wrap and whose
 * window may be only 360 px high. The stack then measures that space itself
 * (ChartStack `fill`); the height here is what it draws until it has. Without
 * `fill` (a mouse at 900 px and wider) this is what it always was.
 */
export function fullscreenChartHeight(
    windowHeight: number,
    fill: boolean
): { height: number; floor: number | undefined } {
    if (!fill) {
        return {
            height: Math.max(
                MIN_FULLSCREEN_CHART_HEIGHT,
                windowHeight - FULLSCREEN_CHROME
            ),
            floor: undefined,
        };
    }
    return {
        height: Math.max(
            FULLSCREEN_MIN_COMPACT,
            windowHeight - FULLSCREEN_CHROME_COMPACT
        ),
        floor: FULLSCREEN_MIN_COMPACT,
    };
}
